import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Cookies from "js-cookie";
import { useForm, Controller } from "react-hook-form";
import { countryCode, CountryList } from "../../apis/api/CountryList";
import { MemberInsert } from "../../apis/api/MemberApi";
import { GetBranches } from "../../apis/api/ProfileApi";
import Lodderr from "../../components/Lodder/Lodder";
import { toast } from "react-toastify";
import "./MemberModals.css";


const AddMember = ({ show, onHide, data, Company_id }) => {
  const dispatch = useDispatch();
  const [preview, setPreview] = useState(null);
  const [branchOptions, setBranchOptions] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const access = JSON.parse(Cookies.get("access") || "{}");

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  const countryCodeData = useSelector((state) => state.countrySlice);
  const countryCodeOptions = countryCodeData?.countryCode?.data?.data || [];
  const { country } = useSelector((state) => state.countrySlice);
  const { loading } = useSelector((state) => state.MemberSlice);
  const titleOptions = ["Mr", "Mrs", "Ms"].map((t) => ({ label: t, value: t }));

  const handleCloseModal = () => {
    reset();
    setSelectedBranch(null);
    setPreview(null);
    onHide();
  };

  useEffect(() => {
    register("ProfilePicture", { required: "Profile picture is required" });
    dispatch(countryCode());
    dispatch(CountryList());
  }, [dispatch, register]);

  useEffect(() => {
    const effectiveCoId = Company_id || Cookies.get("company_id");
    if (effectiveCoId && show) {
      setBranchLoading(true);
      dispatch(GetBranches({ company_id: effectiveCoId }))
        .unwrap()
        .then((res) => {
          if (res?.data && Array.isArray(res.data)) {
            const approved = res.data.filter((b) => b.approve === 1);
            const options = approved.map((b) => ({
              value: b.id,
              label: `${b.city ? b.city + ' - ' : ''}${b.country || ''} (${b.main_branch === 1 ? 'Head Office' : 'Branch Office'})`,
              isHQ: b.main_branch === 1,
            }));
            setBranchOptions(options);

            const hqOption = options.find((o) => o.isHQ) || options[0];
            if (hqOption) {
              setSelectedBranch(hqOption);
              setValue("Branch", hqOption);
            }
          }
        })
        .catch(() => { })
        .finally(() => {
          setBranchLoading(false);
        });
    }
  }, [dispatch, show, Company_id, setValue]);

  useEffect(() => {
    if (data && country?.length) {
      reset({
        Title: { label: data.salutation, value: data.salutation },
        FirstName: data.first_name || "",
        MiddleName: data.middle_name || "",
        LastName: data.last_name || "",
        JobTitle: data.job_title || "",
        Department: data.department || "",
        PhoneDirect: data.contact || "",
        Email: data.email || "",
        CountryCode:
          countryCodeOptions.find((code) => code.value === data.country_code) ||
          null,
        Access: data.access || "",
        ProfilePicture: data.member_logo || null,
        Country: country.find((c) => c.label === data.location) || null,
      });
      setPreview(
        typeof data.member_logo === "string"
          ? data.member_logo
          : data.member_logo instanceof File
            ? URL.createObjectURL(data.member_logo)
            : null
      );
    }
  }, [data, reset, country, countryCodeOptions]);

  const onSubmit = async (formData) => {
    const company_id = Cookies.get("company_id");
    const payload = new FormData();

    const fieldTransform = {
      Title: (val) => val?.value || "",
      CountryCode: (val) => val?.value || "",
      Country: (val) => val?.label || "",
      Branch: (val) => val?.value || "",
      ProfilePicture: (val) => val,
      Access: (val) => val || "",
    };

    for (const key in formData) {
      let value = formData[key];
      if (fieldTransform[key]) {
        value = fieldTransform[key](value);
      }
      if (key === "ProfilePicture") {
        if (value instanceof File) {
          payload.append(key, value);
        }
      } else {
        payload.append(key, value || "");
      }
    }
    if (access.extra_contact === "1") {
      payload.append("contactLimit", access.extra_contact_limit || "");
      payload.append("extra_contact_limit", access.extra_contact_limit || "");
    }
    const branchVal = formData.Branch?.value || selectedBranch?.value;
    if (branchVal) {
      payload.append("BranchId", branchVal);
      payload.append("branch_id", branchVal);
    }
    if (access.extra_contact === "1")
      payload.append("contactLimit", access.extra_contact_limit);
    payload.append("company_id", company_id || "");

    try {
      const response = await dispatch(MemberInsert(payload));
      const resPayload = response?.payload;

      if (resPayload?.status === "success") {
        toast.success("Member added successfully. Please contact the admin for approval.");
      } else {
        const errorMsg =
          resPayload?.message ||
          resPayload?.data?.message ||
          response?.error?.message ||
          "Error in form submission.";
        toast.error(errorMsg);
      }
    } catch (err) {
      toast.error("Error in form submission.");
    }

    handleCloseModal();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreview(URL.createObjectURL(file));
      setValue("ProfilePicture", file, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  return (
    <>
      {loading && <Lodderr />}
      <Modal
        show={show}
        onHide={handleCloseModal}
        size="lg"
        centered
        contentClassName="mm-modal"
      >
        <Modal.Header closeButton className="mm-head">
          <Modal.Title className="mm-title">
            <i className="bx bx-user-plus"></i> Add Member
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="mm-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row gy-4">
              <div className="col-md-12">
                <label className="mm-label">Profile Picture *</label>
                <div
                  className="mm-upload"
                  onClick={() => document.getElementById("member-logo-upload").click()}
                >
                  <input
                    type="file"
                    id="member-logo-upload"
                    accept="image/*"
                    className="d-none"
                    onChange={handleFileChange}
                  />
                  {preview ? (
                    <img
                      src={preview}
                      alt="Preview"
                      className="mm-avatar"
                    />
                  ) : (
                    <>
                      <i className="bx bx-cloud-upload mm-upload-ic"></i>
                      <span className="mm-upload-t">Click to upload</span>
                      <span className="mm-upload-s">PNG/JPG up to 5MB</span>
                    </>
                  )}
                </div>
                {errors.ProfilePicture && (
                  <p className="mm-err">{errors.ProfilePicture.message}</p>
                )}
              </div>

              <div className="col-md-12">
                <label className="mm-label">Title*</label>
                <Controller
                  name="Title"
                  control={control}
                  rules={{ required: "Title is required" }}
                  render={({ field }) => (
                    <Select {...field} options={titleOptions} placeholder="Select Title" classNamePrefix="mm-select" />
                  )}
                />
                {errors.Title && <p className="mm-err">{errors.Title.message}</p>}
              </div>

              {["FirstName", "MiddleName", "LastName"].map((field) => (
                <div className="col-md-4" key={field}>
                  <label className="mm-label">{field !== 'MiddleName' ? field.replace("Name", " Name *") : field.replace("Name", " Name")}</label>
                  <input
                    type="text"
                    className="form-control mm-input"
                    placeholder={
                      field === "FirstName" ? "John" : field === "MiddleName" ? "M." : "Doe"
                    }
                    {...register(field, {
                      required:
                        field !== "MiddleName"
                          ? `${field.replace("Name", " name")} is required`
                          : false,
                    })}
                  />
                  {errors[field] && <p className="mm-err">{errors[field].message}</p>}
                </div>
              ))}

              <div className="col-md-6">
                <label className="mm-label">Job Title*</label>
                <input
                  type="text"
                  className="form-control mm-input"
                  placeholder="Enter job Title here"
                  {...register("JobTitle", { required: "Job title is required" })}
                />
                {errors.JobTitle && <p className="mm-err">{errors.JobTitle.message}</p>}
              </div>

              <div className="col-md-6">
                <label className="mm-label">Department*</label>
                <input
                  type="text"
                  className="form-control mm-input"
                  placeholder="Enter department"
                  {...register("Department", { required: "Department is required" })}
                />
                {errors.Department && <p className="mm-err">{errors.Department.message}</p>}
              </div>

              <div className="col-md-12">
                <label className="mm-label">Email*</label>
                <input
                  type="email"
                  className="form-control mm-input"
                  placeholder="Enter email"
                  {...register("Email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Enter a valid email address",
                    },
                  })}
                />
                {errors.Email && <p className="mm-err">{errors.Email.message}</p>}
              </div>

              <div className="col-md-12">
                <label className="mm-label">Phone Number*</label>
                <div className="d-flex gap-2">
                  <Controller
                    name="CountryCode"
                    control={control}
                    rules={{ required: "Country code is required" }}
                    render={({ field }) => (
                      <Select {...field} className="w-25" options={countryCodeOptions} placeholder="Code" classNamePrefix="mm-select" />
                    )}
                  />
                  <input
                    type="text"
                    className="form-control w-75 mm-input"
                    {...register("PhoneDirect", { required: "Phone number is required" })}
                  />
                </div>
                {errors.CountryCode && <p className="mm-err">{errors.CountryCode.message}</p>}
                {errors.PhoneDirect && <p className="mm-err">{errors.PhoneDirect.message}</p>}
              </div>

              <div className="col-md-12">
                <label className="mm-label">Country*</label>
                <Controller
                  name="Country"
                  control={control}
                  rules={{ required: "Country is required" }}
                  render={({ field }) => (
                    <Select {...field} options={country} placeholder="Select Country" isClearable classNamePrefix="mm-select" />
                  )}
                />
                {errors.Country && <p className="mm-err">{errors.Country.message}</p>}
              </div>

              <div className="col-md-12">
                <label className="mm-label">Branch / Office Location *</label>
                <Controller
                  name="Branch"
                  control={control}
                  rules={{ required: "Branch location is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || selectedBranch}
                      onChange={(val) => {
                        field.onChange(val);
                        setSelectedBranch(val);
                      }}
                      options={branchOptions}
                      placeholder={branchLoading ? "Loading branches..." : "Select Branch Office"}
                      isLoading={branchLoading}
                      isClearable={false}
                      classNamePrefix="mm-select"
                    />
                  )}
                />
                {errors.Branch && <p className="mm-err">{errors.Branch.message}</p>}
              </div>

              <div className="col-md-12">
                <label className="mm-label">Access*</label>
                <Controller
                  name="Access"
                  control={control}
                  rules={{ required: "Access type is required" }}
                  render={({ field }) => (
                    <>
                      <label className={`mm-radio ${field.value === "0" ? "active" : ""}`}>
                        <input
                          className="form-check-input"
                          type="radio"
                          id="admin"
                          value="0"
                          checked={field.value === "0"}
                          onChange={field.onChange}
                        />
                        <span>Admin</span>
                      </label>
                      <label className={`mm-radio ${field.value === "1" ? "active" : ""}`}>
                        <input
                          className="form-check-input"
                          type="radio"
                          id="member"
                          value="1"
                          checked={field.value === "1"}
                          onChange={field.onChange}
                        />
                        <span>Member</span>
                      </label>
                    </>
                  )}
                />
                {errors.Access && <p className="mm-err">{errors.Access.message}</p>}
              </div>

              <div className="col-12">
                <div className="mm-actions">
                  <button type="button" className="mm-btn mm-btn--soft" onClick={handleCloseModal}>
                    Cancel
                  </button>
                  <button type="submit" className="mm-btn" disabled={loading}>
                    {loading ? "Adding..." : "Add Member"}
                    <i className="bx bx-right-arrow-alt"></i>
                  </button>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddMember;